import { useCallback } from 'react';
import AsyncReactCreatableSelect from 'react-select/async-creatable';
import classNames from 'classnames';
import isNil from 'lodash/isNil';

import '@components/mixins/commonStyles.scss';
import '@components/Print/Print_.scss';
import styles from './AsyncCreatableSelect.module.scss';
import '../Select_.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { LeftTitle } from '@components/LeftTitle';
import { NotValidFieldText } from '@components/NotValidFieldText';
import {
    DropdownIndicator,
    getOptionStyles,
    MenuList,
    noOptionsMessage,
    Option,
    OptionType,
    SelectProps,
} from '@components/Select';
import { ClearIndicator } from '@components/Select/Select';
import { translate } from '@chaika/i18n/helper';
import { normalizeYoFilter } from '@utils/normalizeYoString';
import { getMenuPortalProps } from '../getMenuPortalProps';
import { useAsyncCreatableSelect } from './hooks/useAsyncCreatableSelect';

export interface AsyncCreatableSelectProps extends Omit<SelectProps, 'options'> {
    selectedValue?: Nullable<string | number>;
    isLoading?: boolean;
    defaultOptions?: OptionType<string>[];
    name?: string;
    isFilteredOnServer?: boolean;
    createLabelValue?(value: string): string;
    createLabelString?(value: string): string;
    loadOptions(value: string, name?: string): Promise<OptionType<string>[]>;
    onChange(params: Nullable<OptionType>, name?: string): void;
}

export function AsyncCreatableSelect({
    leftTitle,
    title,
    value,
    name,
    disabled,
    placeholder,
    isSearchable = true,
    isClearable = false,
    hideSelectedOptions = true,
    smallMenuWidth,
    isValid,
    notValidText,
    highlightConditionParams,
    createLabelString,
    createLabelValue,
    menuPortalTarget,
    onChange,
    selectedValue,
    loadOptions,
    defaultOptions,
    isFilteredOnServer = false,
    ...props
}: AsyncCreatableSelectProps) {
    const {
        value: resolvedValue,
        placeholder: resolvedPlaceholder,
        handleChange,
        handleCreateOption,
        handleLoadOptions,
        handleFocus,
        handleBlur,
    } = useAsyncCreatableSelect({
        defaultOptions,
        value,
        selectedValue,
        placeholder,
        title,
        name,
        isFilteredOnServer,
        createLabelValue,
        loadOptions,
        onChange,
    });

    const portalProps = getMenuPortalProps(menuPortalTarget, undefined);
    const withValidStatus = !isNil(isValid);

    const formatCreateLabel = useCallback(
        (label: string) =>
            createLabelString ? (
                <div className={classNames([styles.createLabel, 'async_creatable_select__create_label'])}>
                    {createLabelString(label)}
                </div>
            ) : (
                <div>
                    {translate('add')}: {label}
                </div>
            ),
        [createLabelString],
    );

    const select = (
        <div
            className={classNames([
                'component_select__root',
                'component_select__root_alignedValue',
                'font_body',
                smallMenuWidth && 'component_select__root_smallMenuWidth',
            ])}
        >
            {title && !leftTitle && (
                <FieldTitle
                    title={title}
                    theme={FieldTitleTheme.Grey}
                />
            )}
            <AsyncReactCreatableSelect
                {...props}
                {...portalProps}
                placeholder={resolvedPlaceholder ?? '-'}
                value={resolvedValue}
                defaultOptions={defaultOptions}
                hideSelectedOptions={hideSelectedOptions}
                isSearchable={isSearchable}
                isClearable={isClearable}
                filterOption={isFilteredOnServer ? null : normalizeYoFilter}
                isDisabled={disabled}
                noOptionsMessage={noOptionsMessage}
                className={classNames(['component_select__root', 'hide_on_print'])}
                classNamePrefix={'react-select'}
                components={{ DropdownIndicator, MenuList, Option, ClearIndicator }}
                formatCreateLabel={formatCreateLabel}
                styles={{ option: getOptionStyles(highlightConditionParams) }}
                loadOptions={handleLoadOptions}
                onCreateOption={handleCreateOption}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            <div className={classNames(['for_print', 'print_label'])}>{resolvedValue?.label ?? ''}</div>
            {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
        </div>
    );

    return leftTitle && title ? <LeftTitle title={title}>{select}</LeftTitle> : select;
}
